import { Button } from "./button";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import { CheckCircle, XCircle, Info, X } from "lucide-react";

interface ResultDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  details?: { label: string; value: string | number }[];
  variant?: "success" | "error" | "info";
  buttonText?: string;
}

export function ResultDialog({
  open,
  onClose,
  title,
  description,
  details,
  variant = "info",
  buttonText = "OK",
}: ResultDialogProps) {
  if (!open) return null;

  const iconConfig = {
    success: {
      icon: CheckCircle,
      bgColor: "bg-green-500/10",
      iconColor: "text-green-500",
    },
    error: {
      icon: XCircle,
      bgColor: "bg-destructive/10",
      iconColor: "text-destructive",
    },
    info: {
      icon: Info,
      bgColor: "bg-primary/10",
      iconColor: "text-primary",
    },
  };

  const config = iconConfig[variant];
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-start gap-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.bgColor}`}
          >
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg">{title}</CardTitle>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="flex-shrink-0 -mt-1 -mr-2"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          {details && details.length > 0 && (
            <div className="space-y-2 p-3 bg-muted/30 rounded-md">
              {details.map((detail, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{detail.label}</span>
                  <span className="font-medium">{detail.value}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={onClose}>{buttonText}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
